import { DEFAULT_RANGE_SIZE, SCROLL_THRESHOLD, SMART_EDGE_PADDING, SMART_VISIBLE_THRESHOLD } from './constants';

// --- KEY EXTRACTORS ---

export const defaultKeyExtractor = (item, index) => item?.id || `item-${index}`;

export const isHeaderItemDefault = key =>
   typeof key === 'string' && (key === 'encryption-info' || key.startsWith('date-') || key === 'unread-separator');

// --- ITEM HEIGHT ---

export const getItemHeightValue = (item, index, itemHeightsRef, keyExtractor, getItemHeightFn, defaultHeight) => {
   const key = keyExtractor(item, index);
   return itemHeightsRef.current.get(key) || getItemHeightFn?.(item, index) || defaultHeight;
};

// --- OFFSET CALCULATION ---

export const calculateOffset = (
   items,
   startIndex,
   endIndex,
   itemHeightsRef,
   keyExtractor,
   getItemHeightFn,
   defaultHeight,
) => {
   let offset = 0;
   for (let i = startIndex; i < endIndex; i++) {
      const item = items[i];
      if (!item) continue;
      offset += getItemHeightValue(item, i, itemHeightsRef, keyExtractor, getItemHeightFn, defaultHeight);
   }
   return offset;
};

// --- RANGE HELPERS ---

export const calculateInitialRange = (items, initialScrollKey, keyExtractor) => {
   if (initialScrollKey && items.length > 0) {
      const targetIndex = findItemIndex(items, keyExtractor, initialScrollKey);
      if (targetIndex !== -1) {
         return {
            start: Math.max(0, targetIndex - DEFAULT_RANGE_SIZE),
            end: Math.min(items.length, targetIndex + DEFAULT_RANGE_SIZE + 1),
         };
      }
   }
   return { start: 0, end: Math.min(DEFAULT_RANGE_SIZE, items.length) };
};

export const clampRange = (start, end, itemsLength) => {
   if (start >= end && itemsLength > 0) {
      return { start: Math.max(0, itemsLength - DEFAULT_RANGE_SIZE), end: itemsLength };
   }
   return { start, end };
};

export const findVisibleRangeStart = (
   items,
   scrollTop,
   overscan,
   itemHeightsRef,
   keyExtractor,
   getItemHeightFn,
   itemHeight,
) => {
   let currentTop = 0;
   for (let i = 0; i < items.length; i++) {
      const height = getItemHeightValue(items[i], i, itemHeightsRef, keyExtractor, getItemHeightFn, itemHeight);
      if (currentTop + height > scrollTop) return Math.max(0, i - overscan);
      currentTop += height;
   }
   return 0;
};

export const findVisibleRangeEnd = (
   items,
   visibleBottom,
   overscan,
   itemHeightsRef,
   keyExtractor,
   getItemHeightFn,
   itemHeight,
) => {
   let currentTop = 0;
   for (let i = 0; i < items.length; i++) {
      const height = getItemHeightValue(items[i], i, itemHeightsRef, keyExtractor, getItemHeightFn, itemHeight);
      currentTop += height;
      if (currentTop >= visibleBottom) return Math.min(items.length, i + 1 + overscan);
   }
   return items.length;
};

// --- ITEM LOOKUP ---

export const findItemIndex = (items, keyExtractor, targetKey) =>
   items.findIndex((item, idx) => {
      const itemKey = keyExtractor(item, idx);
      return itemKey === targetKey || (typeof itemKey === 'string' && itemKey.includes(targetKey));
   });

// --- SCROLL ---

export const calculateScrollPosition = (offsetToTarget, targetHeight, containerHeight, block, maxScroll) => {
   const positions = {
      center: offsetToTarget + targetHeight / 2 - containerHeight / 2,
      start: offsetToTarget,
      end: offsetToTarget + targetHeight - containerHeight,
      bottom: offsetToTarget + targetHeight - containerHeight,
   };
   const scrollPosition = positions[block] || positions.center;
   return Math.max(0, Math.min(scrollPosition, maxScroll));
};

export const isAtBottom = (container, inverted = false, threshold = SCROLL_THRESHOLD) => {
   if (inverted) return Math.abs(container.scrollTop) <= threshold;
   return container.scrollTop + container.clientHeight >= container.scrollHeight - threshold;
};

// --- SMART SCROLL ---
//
// block values:
//   'smart'        → auto-detect based on where element is in the viewport
//   'center'       → always center
//   'start'        → align to top
//   'end'/'bottom' → align to bottom
//   0.0 – 1.0      → numeric: 0=top, 0.5=center, 1=bottom
//
// Smart logic:
//   fully visible           → no scroll (keep in place)
//   clipped/out at top      → snap near top + SMART_EDGE_PADDING
//   clipped/out at bottom   → end alignment (natural for chat reply-jumps)
//
export const resolveSmartTargetVisualTop = (elementVisualTop, elementHeight, containerHeight) => {
   const elementBottom = elementVisualTop + elementHeight;
   const isAboveViewport = elementBottom < 0;
   const isBelowViewport = elementVisualTop > containerHeight;
   const isClippedAtTop = elementVisualTop < SMART_VISIBLE_THRESHOLD;
   const isClippedAtBottom = elementBottom > containerHeight - SMART_VISIBLE_THRESHOLD;

   if (isAboveViewport || (isClippedAtTop && !isClippedAtBottom)) return SMART_EDGE_PADDING;
   if (isBelowViewport || isClippedAtBottom) return containerHeight - elementHeight - SMART_EDGE_PADDING;
   return elementVisualTop; // Fully visible — no scroll
};

// --- ASYNC UTILS ---

export const waitForAnimationFrame = () => new Promise(resolve => requestAnimationFrame(resolve));
export const waitForTimeout = ms => new Promise(resolve => setTimeout(resolve, ms));
